import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function StandardGeneratedAssessmentsPage() {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ subject: "all", difficulty: "all", teacher: "all" });
  const navigate = useNavigate();

  // Function to export/download assessment as PDF - MOVED TO TOP
  const exportAssessment = async (assessment) => {
    try {
      console.log("📤 Exporting assessment:", assessment);
      
      // First, fetch the complete assessment data with questions
      const res = await fetch(`/api/generated-assessments/standard/${assessment._id}`);
      if (!res.ok) throw new Error("Failed to fetch assessment details");
      
      const fullAssessment = await res.json();
      console.log("📊 Full assessment data:", fullAssessment);

      // Create a printable HTML content
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${fullAssessment.assessmentName || "Untitled Assessment"}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
            .header { border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
            .assessment-title { font-size: 24px; font-weight: bold; color: #333; }
            .assessment-meta { color: #666; margin: 10px 0; }
            .question { margin: 25px 0; padding: 15px; border: 1px solid #ddd; border-radius: 8px; }
            .question-number { font-weight: bold; color: #7c3aed; margin-bottom: 10px; }
            .options { margin: 10px 0 0 20px; }
            .option { margin: 5px 0; }
            .correct-answer { color: #059669; font-weight: bold; margin-top: 10px; padding: 5px; background: #f0fdf4; border-radius: 4px; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="assessment-title">${fullAssessment.assessmentName || "Untitled Assessment"}</div>
            <div class="assessment-meta">
              <strong>Subject:</strong> ${fullAssessment.subject || "N/A"} | 
              <strong> Grade Level:</strong> ${fullAssessment.gradeLevel || "N/A"} | 
              <strong> Difficulty:</strong> ${fullAssessment.difficulty || "N/A"} | 
              <strong> Questions:</strong> ${fullAssessment.questionsCount || 0}
            </div>
            <div class="assessment-meta">
              <strong>Teacher:</strong> ${fullAssessment.teacher?.name || "Unknown"} | 
              <strong> Created:</strong> ${fullAssessment.createdAt ? new Date(fullAssessment.createdAt).toLocaleDateString() : 'N/A'}
            </div>
          </div>

          ${fullAssessment.questions && fullAssessment.questions.length > 0 ? 
            fullAssessment.questions.map((question, index) => `
              <div class="question">
                <div class="question-number">Question ${index + 1}</div>
                <div>${question.questionText || "No question text available"}</div>
                ${question.options && question.options.length > 0 ? `
                  <div class="options">
                    ${question.options.map((option, optIndex) => `
                      <div class="option">
                        ${String.fromCharCode(65 + optIndex)}. ${option || "No option text"}
                        ${question.correctAnswer === String.fromCharCode(65 + optIndex) ? ' <span style="color: #059669;">✅ Correct</span>' : ''}
                      </div>
                    `).join('')}
                  </div>
                ` : '<div class="options">No options available</div>'}
                ${question.correctAnswer ? `
                  <div class="correct-answer">Correct Answer: ${question.correctAnswer}</div>
                ` : ''}
              </div>
            `).join('') 
            : '<div class="question"><p>No questions available for this assessment.</p></div>'
          }

          <div class="footer">
            Generated by AssessaAI on ${new Date().toLocaleDateString()} | ${window.location.origin}
          </div>
        </body>
        </html>
      `;

      // Create a hidden iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.write(printContent);
      iframeDoc.close();
      
      // Wait for content to load then print
      iframe.onload = function() {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          
          // Remove iframe after printing
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
        }, 500);
      };

    } catch (error) {
      console.error("Error exporting assessment:", error);
      
      // Fallback: Try with basic data using iframe
      try {
        const basicContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>${assessment.assessmentName || "Untitled Assessment"}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
              .header { border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
              .assessment-title { font-size: 24px; font-weight: bold; color: #333; }
              .assessment-meta { color: #666; margin: 10px 0; }
              .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px; }
            </style>
          </head>
          <body>
            <div class="header">
              <div class="assessment-title">${assessment.assessmentName || "Untitled Assessment"}</div>
              <div class="assessment-meta">
                <strong>Subject:</strong> ${assessment.subject || "N/A"} | 
                <strong> Grade Level:</strong> ${assessment.gradeLevel || "N/A"} | 
                <strong> Difficulty:</strong> ${assessment.difficulty || "N/A"} | 
                <strong> Questions:</strong> ${assessment.questionsCount || 0}
              </div>
              <div class="assessment-meta">
                <strong>Teacher:</strong> ${assessment.teacher?.name || "Unknown"} | 
                <strong> Created:</strong> ${assessment.createdAt ? new Date(assessment.createdAt).toLocaleDateString() : 'N/A'}
              </div>
            </div>

            <div style="text-align: center; padding: 40px; color: #666;">
              <p>Questions data not available for export.</p>
              <p>Please use the Preview feature to view questions.</p>
            </div>

            <div class="footer">
              Generated by AssessaAI on ${new Date().toLocaleDateString()} | ${window.location.origin}
            </div>
          </body>
          </html>
        `;

        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.write(basicContent);
        iframeDoc.close();
        
        iframe.onload = function() {
          setTimeout(() => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            
            setTimeout(() => {
              document.body.removeChild(iframe);
            }, 1000);
          }, 500);
        };
        
      } catch (fallbackError) {
        console.error("Fallback export also failed:", fallbackError);
        alert("Failed to export assessment. Please try the Preview feature instead.");
      }
    }
  };

  // Alternative: Download as text file - ALSO MOVED UP
  const downloadAsText = (assessment) => {
    try {
      let content = `ASSESSMENT: ${assessment.assessmentName || "Untitled Assessment"}\n`;
      content += `Subject: ${assessment.subject || "N/A"}\n`;
      content += `Grade Level: ${assessment.gradeLevel || "N/A"}\n`;
      content += `Difficulty: ${assessment.difficulty || "N/A"}\n`;
      content += `Teacher: ${assessment.teacher?.name || "Unknown"}\n`;
      content += `Created: ${assessment.createdAt ? new Date(assessment.createdAt).toLocaleDateString() : 'N/A'}\n`;
      content += `Questions: ${assessment.questionsCount || 0}\n\n`;
      
      if (assessment.questions && assessment.questions.length > 0) {
        assessment.questions.forEach((question, index) => {
          content += `QUESTION ${index + 1}:\n`;
          content += `${question.questionText || "No question text"}\n`;
          
          if (question.options && question.options.length > 0) {
            content += `Options:\n`;
            question.options.forEach((option, optIndex) => {
              const isCorrect = question.correctAnswer === String.fromCharCode(65 + optIndex);
              content += `  ${String.fromCharCode(65 + optIndex)}. ${option}${isCorrect ? ' [CORRECT]' : ''}\n`;
            });
          }
          
          if (question.correctAnswer) {
            content += `Correct Answer: ${question.correctAnswer}\n`;
          }
          content += `\n`;
        });
      } else {
        content += "No questions available for this assessment.\n";
      }
      
      content += `\nExported from AssessaAI on ${new Date().toLocaleDateString()}`;

      // Create and download file
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${assessment.assessmentName || 'assessment'}_${new Date().getTime()}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error("Error downloading assessment:", error);
      alert("Failed to download assessment. Please try again.");
    }
  };

  useEffect(() => {
    const fetchStandard = async () => {
      try {
        const res = await fetch("/api/generated-assessments/standard");
        const data = await res.json();

        console.log("📊 Standard Assessments API Response:", data);

        if (Array.isArray(data)) {
          setAssessments(data);
        } else {
          setAssessments([]);
        }
      } catch (err) {
        console.error("Error fetching standard assessments", err);
        setAssessments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStandard();
  }, []);

  const filtered = assessments.filter((a) => {
    if (filter.subject !== "all" && (a.subject || "").toLowerCase() !== filter.subject.toLowerCase()) return false;
    if (filter.difficulty !== "all" && (a.difficulty || "").toLowerCase() !== filter.difficulty.toLowerCase()) return false;
    if (filter.teacher !== "all" && (a.teacher?.name || "").toLowerCase().indexOf(filter.teacher.toLowerCase()) === -1) return false;
    return true;
  });

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "easy": return "bg-gradient-to-r from-emerald-400 to-emerald-600 text-white";
      case "medium": return "bg-gradient-to-r from-amber-400 to-amber-600 text-white";
      case "hard": return "bg-gradient-to-r from-orange-400 to-orange-600 text-white";
      case "very hard": return "bg-gradient-to-r from-rose-400 to-rose-600 text-white";
      default: return "bg-gradient-to-r from-gray-400 to-gray-600 text-white";
    }
  };

  const getSubjectIcon = (subject) => {
    switch (subject?.toLowerCase()) {
      case "math": return (
        <div className="p-2 bg-blue-100 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
      );
      case "science": return (
        <div className="p-2 bg-green-100 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        </div>
      );
      case "english": return (
        <div className="p-2 bg-purple-100 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </div>
      );
      case "history": return (
        <div className="p-2 bg-amber-100 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      );
      default: return (
        <div className="p-2 bg-indigo-100 rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading standard assessments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <button 
              onClick={() => navigate('/admin-dashboard')}
              className="flex items-center text-amber-600 hover:text-amber-700 transition-colors mb-4 font-medium"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Dashboard
            </button>
            <h1 className="text-3xl font-bold text-cyan-600">Standard Generated Assessments</h1>
            <p className="text-gray-600 mt-2">Browse and manage all standard curriculum assessments</p>
          </div>
          
          <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-200">
            <span className="text-sm font-medium text-gray-700">
              Showing <span className="text-purple-600 font-bold">{filtered.length}</span> of <span className="text-purple-600 font-bold">{assessments.length}</span> assessments
            </span>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-2xl shadow-md p-6 mb-8 border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filter Assessments
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <select
                value={filter.subject}
                onChange={(e) => setFilter((p) => ({ ...p, subject: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition bg-white"
              >
                <option value="all">All Subjects</option>
                <option value="math">Math</option>
                <option value="science">Science</option>
                <option value="english">English</option>
                <option value="history">History</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
              <select
                value={filter.difficulty}
                onChange={(e) => setFilter((p) => ({ ...p, difficulty: e.target.value }))}
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition bg-white"
              >
                <option value="all">All Difficulties</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
                <option value="very hard">Very Hard</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Teacher</label>
              <div className="relative">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  placeholder="Search by teacher"
                  value={filter.teacher}
                  onChange={(e) => setFilter((p) => ({ ...p, teacher: e.target.value }))}
                  className="w-full p-3 pl-10 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition bg-white"
                />
              </div>
            </div>

            <div className="flex items-end">
              <button 
                onClick={() => setFilter({ subject: "all", difficulty: "all", teacher: "all" })}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3.5 px-4 rounded-xl transition font-medium flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Assessments Grid */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center border border-gray-100">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No assessments found</h3>
            <p className="text-gray-600">Try adjusting your filters to see more results</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((a) => (
              <div
                key={a._id}
                className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition duration-300 border border-gray-100 flex flex-col"
              >
                <div className="p-6 flex-1">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center">
                      {getSubjectIcon(a.subject)}
                      <div className="ml-3">
                        <h3 className="font-bold text-gray-900">{a.assessmentName || "Untitled Assessment"}</h3>
                        <p className="text-sm text-gray-500">
                          {a.subject || "N/A"} | Grade {a.gradeLevel || "N/A"}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${getDifficultyColor(a.difficulty)}`}>
                      {a.difficulty || "N/A"}
                    </span>
                  </div>

                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="flex items-center mr-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        {a.questionsCount ?? 0} questions
                      </span>
                      {a.estimatedTime && (
                        <span className="flex items-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {a.estimatedTime} min
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center">
                      {typeof a.rating === "number" ? (
                        <div className="flex items-center bg-amber-50 text-amber-700 px-2.5 py-1 rounded-lg text-xs font-medium">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                          </svg>
                          {a.rating.toFixed(1)}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No rating</span>
                      )}
                    </div>
                  </div>

                  <div className="mb-4 flex flex-wrap gap-2">
                    {(Array.isArray(a.tags) ? a.tags : []).slice(0, 4).map((tag, idx) => (
                      <span key={idx} className="bg-gray-100 text-gray-700 px-2.5 py-1 rounded-lg text-xs">
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="border-t border-gray-100 pt-4 mb-4">
                    <div className="flex items-center">
                      <div className="w-9 h-9 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{a.teacher?.name || 'Unknown Teacher'}</p>
                        <p className="text-xs text-gray-500">{a.teacher?.email || ''}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => navigate(`/admin-dashboard/standard-assessment-preview/${a._id}`)}
                      className="py-2.5 px-4 bg-white border border-purple-600 text-purple-600 rounded-xl hover:bg-purple-50 transition font-medium text-sm flex items-center justify-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Preview
                    </button>
                    <button
                      onClick={() => exportAssessment(a)}
                      className="py-2.5 px-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition font-medium text-sm flex items-center justify-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Export
                    </button>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100">
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <div className="flex space-x-4">
                      <span className="flex items-center font-medium">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {a.stats?.attempts ?? 0} attempts
                      </span>
                      <span className="flex items-center font-medium">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        {typeof a.stats?.avgScore === "number" ? `${a.stats.avgScore}%` : "N/A"} avg
                      </span>
                    </div>
                    <span>{a.createdAt ? new Date(a.createdAt).toLocaleDateString() : 'N/A'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}